import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axiosInstance from '../../utils/axiosInstance';
import { normalizeAdvice, normalizeFloweringArch } from '../../utils/normalize';
import { floweringArchTypesEnum } from './types';
import { setErrorMessage } from '../genericError';
import { restartSession } from '../session';

export const getFloweringArch = createAsyncThunk(
  'floweringArch/getFloweringArch',
  async (floweringArchType, { dispatch, getState, rejectWithValue }) => {
    const { bees, plants, floweringArch: floweringArchState } = getState();
    const { selectedBeeIds } = bees;
    const { presentAccordingToUserIds, pickedFromAdviceIds } = plants;
    const { coverageGoal } = floweringArchState;

    if (selectedBeeIds.length === 0) {
      return rejectWithValue(`Array length of selectedBeeIds should be larger than 0, recieved: ${selectedBeeIds}`);
    }

    let plantIdsForFloweringArch = presentAccordingToUserIds;
    if (floweringArchType === floweringArchTypesEnum.basedOnAdvise) {
      plantIdsForFloweringArch = pickedFromAdviceIds;
    }

    let coverage;
    let floweringArch;
    try {
      const result = await axiosInstance.get('coverage/flowering-arch', {
        params: {
          beeIds: JSON.stringify(selectedBeeIds),
          plantIds: JSON.stringify(plantIdsForFloweringArch),
          coverageGoal,
        },
      });

      floweringArch = result.data.data.floweringArch;
      coverage = result.data.data.coverage;
    } catch (err) {
      if (!err.response) {
        return dispatch(setErrorMessage('Server onbereikbaar'));
      }

      return dispatch(setErrorMessage(err.response.data.message));
    }

    return {
      coverage,
      floweringArch,
      floweringArchType,
    };
  },
);

export const getAdvice = createAsyncThunk(
  'floweringArch/getAdvice',
  async (floweringArchType, { dispatch, getState, rejectWithValue }) => {
    const { bees, plants, floweringArch } = getState();
    const { selectedBeeIds } = bees;
    const { ids, pickedFromAdviceIds } = plants;
    const { coverageGoal } = floweringArch;

    const unPickedPlantIds = ids.filter((id) => !pickedFromAdviceIds.includes(id));

    if (selectedBeeIds.length === 0) {
      return rejectWithValue(`Array length of selectedBeeIds should be larger than 0, recieved: ${selectedBeeIds}`);
    }

    let advice;
    try {
      const result = await axiosInstance.get('coverage/plant-advice', {
        params: {
          beeIds: JSON.stringify(selectedBeeIds),
          selectedPlantIds: JSON.stringify(pickedFromAdviceIds),
          unselectedPlantIds: JSON.stringify(unPickedPlantIds),
          coverageGoal,
        },
      });

      advice = result.data.data.advice;
    } catch (err) {
      if (!err.response) {
        return dispatch(setErrorMessage('Server onbereikbaar'));
      }

      return dispatch(setErrorMessage(err.response.data.message));
    }

    return {
      advice,
    };
  },
);

const initialState = {
  coverageGoal: 5,
  basedOnCurrent: {
    arch: null,
    coverage: {
      overall: null,
      perDatepart: null,
      perBee: null,
    },
    minMonth: null,
    maxMonth: null,
  },
  basedOnAdvise: {
    arch: null,
    coverage: {
      overall: null,
      perDatepart: null,
      perBee: null,
    },
    minMonth: null,
    maxMonth: null,
  },
  advice: {
    isLoading: false,
    forPlants: null,
  },
};

export const floweringArch = createSlice({
  name: 'floweringArch',
  initialState,
  reducers: {
    setFloweringArch: (state, action) => {
      state.arch = action.payload;
    },
  },
  extraReducers: {
    [restartSession.fulfilled]: () => initialState,
    [getFloweringArch.fulfilled]: (state, action) => {
      const {
        coverage,
        floweringArch: arch,
        floweringArchType,
      } = action.payload;

      const {
        values: normalizedArch,
        minMonth,
        maxMonth,
      } = normalizeFloweringArch(arch);

      state[floweringArchType].coverage = coverage;
      state[floweringArchType].arch = normalizedArch;
      state[floweringArchType].minMonth = minMonth;
      state[floweringArchType].maxMonth = maxMonth;
    },
    [getAdvice.pending]: (state) => {
      state.advice.loading = true;
    },
    [getAdvice.rejected]: (state) => {
      state.advice.loading = false;
    },
    [getAdvice.fulfilled]: (state, action) => {
      const {
        advice,
      } = action.payload;

      state.advice.forPlants = normalizeAdvice(advice);
      state.advice.isLoading = false;
    },
  },
});

export const selectCoverageGoal = (state) => state.floweringArch.coverageGoal;
export const selectCurrentFloweringArch = (state) => state.floweringArch.basedOnCurrent;
export const selectWithAdviceFloweringArch = (state) => state.floweringArch.basedOnAdvise;
export const selectAdvice = (state) => state.floweringArch.advice.forPlants;
export const selectAdviceIsLoading = (state) => state.floweringArch.advice.isLoading;

export default floweringArch.reducer;
